import axios, { type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'

import { type IResponseInbox } from 'entities/Inbox/api/types'
import type {
  IRcsComplianceCreateParams,
  IRcsComplianceStepParams,
  IRcsVideoUploadLink,
  IRcsVideoUploadLinkParams,
  IResponseRcsCompliance,
} from './rcsComplianceTypes'
import type {
  IRcsUpdateParams,
  IResponseRcs,
  IRcsUploadLogoParams,
  IResponseRcsUploadLogo,
  IRcsRemoveLogoParams,
  IResponseRcsRemoveLogo,
  IRcsUploadBannerParams,
  IResponseRcsUploadBanner,
  IRcsRemoveBannerParams,
  IResponseRcsRemoveBanner,
  IRcsUploadOptInImageParams,
  IResponseRcsUploadOptInImage,
  IRcsParams,
  IResponseRcsFallbackInbox,
  IResponseRcsMessageType,
  IResponseRcsCompanyType,
  IResponseRcsBusinessIndustry,
  IResponseRcsUseCase,
} from './types'

class Api {
  updateRcs(params: IRcsUpdateParams): Promise<AxiosResponse<IResponseRcsFallbackInbox>> {
    return baseApi.post(`rcs-sender/${params.id}/fallback-inbox`, params)
  }

  getRcs(options?: AxiosOptions): Promise<AxiosResponse<IResponseRcs[]>> {
    return baseApi.get('rcs-sender/list', { ...options })
  }

  getRcsOrganizationInbox(options?: AxiosOptions): Promise<AxiosResponse<IResponseInbox[]>> {
    return baseApi.get('rcs-sender/inboxes', { ...options })
  }

  uploadLogo(params: IRcsUploadLogoParams): Promise<AxiosResponse<IResponseRcsUploadLogo>> {
    return baseApi.post('rcs-sender/logo', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  removeLogo(params: IRcsRemoveLogoParams): Promise<AxiosResponse<IResponseRcsRemoveLogo>> {
    return baseApi.delete('rcs-sender/logo', { params })
  }

  uploadBanner(params: IRcsUploadBannerParams): Promise<AxiosResponse<IResponseRcsUploadBanner>> {
    return baseApi.post('rcs-sender/banner', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  removeBanner(params: IRcsRemoveBannerParams): Promise<AxiosResponse<IResponseRcsRemoveBanner>> {
    return baseApi.delete('rcs-sender/banner', { params })
  }

  saveRcs(params: IRcsParams): Promise<AxiosResponse<IResponseRcs>> {
    return baseApi.post('rcs-sender/save', params)
  }

  /* ------ RCS Compliance ------ */

  createRcsCompliance(
    params: IRcsComplianceCreateParams
  ): Promise<AxiosResponse<IResponseRcsCompliance>> {
    return baseApi.post('compliance/rcs/compliance', params)
  }

  updateRcsComplianceStep(
    id: number,
    step: number,
    params?: IRcsComplianceStepParams
  ): Promise<AxiosResponse<IResponseRcsCompliance>> {
    return baseApi.put(`compliance/rcs/compliance/${id}`, { step, ...params })
  }

  getRcsCompliance(options?: AxiosOptions): Promise<AxiosResponse<IResponseRcsCompliance>> {
    return baseApi.get('compliance/rcs/compliance', { ...options })
  }

  deleteRcsCompliance(id: number): Promise<AxiosResponse<void>> {
    return baseApi.delete(`compliance/rcs/compliance/${id}`)
  }

  getRcsComplianceById(
    id: number,
    options?: AxiosOptions
  ): Promise<AxiosResponse<{ data: IResponseRcsCompliance }>> {
    return baseApi.get(`admin/core/organizations/rcs/compliance/${id}`, { ...options })
  }

  getMessageTypes(options?: AxiosOptions): Promise<AxiosResponse<IResponseRcsMessageType[]>> {
    return baseApi.get('compliance/rcs/message-type', { ...options })
  }

  getCompanyTypes(options?: AxiosOptions): Promise<AxiosResponse<IResponseRcsCompanyType[]>> {
    return baseApi.get('compliance/rcs/company-type', { ...options })
  }

  getBusinessIndustries(
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseRcsBusinessIndustry[]>> {
    return baseApi.get('compliance/rcs/business-industry', { ...options })
  }

  getUseCases(options?: AxiosOptions): Promise<AxiosResponse<IResponseRcsUseCase[]>> {
    return baseApi.get('compliance/rcs/use-case', { ...options })
  }

  uploadOptInImage(
    params: IRcsUploadOptInImageParams
  ): Promise<AxiosResponse<IResponseRcsUploadOptInImage>> {
    return baseApi.post('rcs-sender/opt-in-image', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  getRcsVideoUploadLink(
    params: IRcsVideoUploadLinkParams
  ): Promise<AxiosResponse<IRcsVideoUploadLink>> {
    return baseApi.post('rcs-sender/use-case-video/upload-link', params)
  }

  uploadFileToStorage(
    url: string,
    file: File,
    headers: Record<string, string[]>
  ): Promise<AxiosResponse<void>> {
    return axios.put(url, file, { headers })
  }
}

export const RcsApi = new Api()
